import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@context/CartContext';
import { crmService } from '@services/crmService';

// Styled Components
const NavbarContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  transition: all ${({ theme }) => theme.transitions.default};
  padding: ${({ $isScrolled, theme }) => $isScrolled ? theme.spacing['2'] : theme.spacing['4']} 0;

  /* Solid paper masthead with an ink rule — the old transparent/blur header
     belonged to the dark design and rendered as a gray smear over the cream. */
  background-color: ${({ theme }) => theme.colors.background};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};

  ${({ $isScrolled, theme }) => $isScrolled && css`
    box-shadow: ${theme.shadows.md};
  `}
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing['4']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 ${({ theme }) => theme.spacing['3']};
  }
`;

const LogoContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const Logo = styled(Link)<{ $isScrolled: boolean }>`
  display: block;
  text-decoration: none;

  /* Two-deck record-label masthead: serif name over letterspaced gothic
     descriptor — keeps the long brand compact at every viewport width. */
  h1 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: ${({ $isScrolled }) => $isScrolled ? '1.25rem' : '1.45rem'};
    font-weight: 700;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    letter-spacing: -0.02em;
    line-height: 1;
    transition: font-size ${({ theme }) => theme.transitions.default};

    span {
      display: block;
      font-family: ${({ theme }) => theme.fonts.ui};
      font-size: 0.52em;
      font-weight: 600;
      letter-spacing: 0.34em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.accent1};
      margin-top: 0.3em;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      font-size: ${({ $isScrolled }) => $isScrolled ? '1.1rem' : '1.25rem'};
    }
  }
`;

const NavLinks = styled.nav<{ $isOpen: boolean }>`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    background: ${({ theme }) => theme.colors.surface};
    transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform ${({ theme }) => theme.transitions.default};
    z-index: ${({ theme }) => theme.zIndex.modal};
    padding: 100px ${({ theme }) => theme.spacing['5']} ${({ theme }) => theme.spacing['5']};
    display: flex;
    flex-direction: column;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing['4']};
  }
`;

const NavItem = styled.li<{ $isActive?: boolean }>`
  margin: 0 ${({ theme }) => theme.spacing['3']};
  position: relative;
  
  ${({ $isActive }) => $isActive && css`
    &::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--color-primary);
    }
  `}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin: 0;
    
    ${({ $isActive }) => $isActive && css`
      &::after {
        display: none;
      }
    `}
  }
`;

const NavLink = styled(Link)<{ $isActive?: boolean }>`
  color: ${({ theme, $isActive }) => $isActive ? theme.colors.primary : theme.colors.text};
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: ${({ theme, $isActive }) => $isActive ? theme.fontWeights.bold : theme.fontWeights.medium};
  transition: all ${({ theme }) => theme.transitions.quick};
  position: relative;
  padding: ${({ theme }) => `${theme.spacing['1']} ${theme.spacing['2']}`};
  display: block;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform ${({ theme }) => theme.transitions.default};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
  
  ${({ $isActive }) => $isActive && css`
    &::before {
      transform: scaleX(1);
    }
  `}
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 1.3rem;
    padding: ${({ theme }) => theme.spacing['2']} 0;
    
    ${({ $isActive }) => $isActive && css`
      color: var(--color-primary);
      
      &::before {
        height: 0;
      }
    `}
  }
`;

const CartButton = styled.button<{ $hasItems: boolean }>`
  background: none;
  border: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;
  margin-left: ${({ theme }) => theme.spacing['3']};
  z-index: 2;

  /* Narrow viewports get exactly one control (the hamburger); the inquiry
     entry lives inside the slide-out menu instead. */
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
  
  svg {
    width: 28px;
    height: 28px;
    fill: ${({ theme }) => theme.colors.text};
    transition: all ${({ theme }) => theme.transitions.quick};
  }
  
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.primary};
      transform: scale(1.1);
    }
  }
  
  ${({ $hasItems, theme }) => $hasItems && css`
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      right: -2px;
      width: 8px;
      height: 8px;
      background-color: ${theme.colors.secondary};
      border-radius: 50%;
    }
  `}
`;

const CartCount = styled.span<{ $count: number }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 18px;
  height: 18px;
  background-color: ${({ theme }) => theme.colors.accent1};
  color: ${({ theme }) => theme.colors.surface};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $count }) => $count > 0 ? 'scale(1)' : 'scale(0)'};
  transition: transform ${({ theme }) => theme.transitions.quick};
  z-index: 1;
`;

const MenuInquiryButton = styled.button`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
    width: 100%;
    margin-top: ${({ theme }) => theme.spacing['5']};
    padding: ${({ theme }) => theme.spacing['3']};
    background: ${({ theme }) => theme.colors.accent1};
    color: ${({ theme }) => theme.colors.surface};
    border: 2px solid ${({ theme }) => theme.colors.primary};
    border-radius: 4px;
    box-shadow: ${({ theme }) => theme.shadows.sm};
    font-size: ${({ theme }) => theme.fontSizes.md};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  position: relative;
  cursor: pointer;
  z-index: 10;
  
  span {
    display: block;
    position: absolute;
    height: 3px;
    width: 100%;
    background: ${({ theme }) => theme.colors.text};
    border-radius: 3px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: ${({ theme }) => theme.transitions.default};
    
    &:nth-child(1) {
      top: ${({ $isOpen }) => $isOpen ? '18px' : '10px'};
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(135deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      top: 18px;
      opacity: ${({ $isOpen }) => $isOpen ? '0' : '1'};
      transform: ${({ $isOpen }) => $isOpen ? 'translateX(20px)' : 'translateX(0)'};
    }
    
    &:nth-child(3) {
      top: ${({ $isOpen }) => $isOpen ? '18px' : '26px'};
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(-135deg)' : 'rotate(0)'};
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  z-index: 5;
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

// Props interface
interface NavbarProps {
  toggleCart: () => void;
}

// Navbar component
const Navbar: React.FC<NavbarProps> = ({ toggleCart }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Listen for scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Cache admin check result to avoid redundant API calls on route change
  const lastTokenChecked = useRef<string | null>(null);

  // Detect admin auth - only re-check when token changes
  useEffect(() => {
    const token = sessionStorage.getItem('crm_token');

    // Skip API call if token hasn't changed since last check
    if (token === lastTokenChecked.current) {
      return;
    }

    const checkAdmin = async () => {
      lastTokenChecked.current = token;

      if (!token) {
        setIsAdmin(false);
        return;
      }
      try {
        const resp = await crmService.getCurrentUser();
        if (resp.user && resp.user.legacy_token !== true) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch {
        setIsAdmin(false);
        lastTokenChecked.current = null; // Reset on error to allow retry
      }
    };
    checkAdmin();
  }, [location.pathname]);
  
  // Pre-compute active state for all routes (avoids repeated function calls)
  const activePath = location.pathname;
  const isProjectsActive = activePath === '/mad-magazine' || activePath === '/forbidden-elvis';

  return (
    <NavbarContainer $isScrolled={isScrolled}>
      <NavContent>
        <LogoContainer>
          <Logo to="/" $isScrolled={isScrolled}>
            <h1>Blagman Brothers <span>Vintage Music</span></h1>
          </Logo>
        </LogoContainer>
        
        <MobileMenuButton
          $isOpen={isMobileMenuOpen}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuButton>
        
        <AnimatePresence>
          {isMobileMenuOpen && (
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </AnimatePresence>
        
        <NavLinks $isOpen={isMobileMenuOpen}>
          <NavList>
            <NavItem $isActive={activePath === '/'}>
              <NavLink to="/" $isActive={activePath === '/'}>
                Home
              </NavLink>
            </NavItem>
            <NavItem $isActive={activePath === '/catalog'}>
              <NavLink to="/catalog" $isActive={activePath === '/catalog'}>
                Catalog
              </NavLink>
            </NavItem>
            <NavItem $isActive={activePath === '/about'}>
              <NavLink to="/about" $isActive={activePath === '/about'}>
                About Us
              </NavLink>
            </NavItem>
            <NavItem $isActive={isProjectsActive}>
              <NavLink to="/mad-magazine" $isActive={isProjectsActive}>
                Projects
              </NavLink>
            </NavItem>
            <NavItem $isActive={activePath === '/licensing'}>
              <NavLink to="/licensing" $isActive={activePath === '/licensing'}>
                Licensing
              </NavLink>
            </NavItem>
            <NavItem $isActive={activePath === '/contact'}>
              <NavLink to="/contact" $isActive={activePath === '/contact'}>
                Contact
              </NavLink>
            </NavItem>
            {isAdmin && (
              <>
                <NavItem $isActive={activePath === '/admin/tracks'}>
                  <NavLink to="/admin/tracks" $isActive={activePath === '/admin/tracks'}>
                    Tracks CRM
                  </NavLink>
                </NavItem>
                <NavItem $isActive={activePath === '/admin/account'}>
                  <NavLink to="/admin/account" $isActive={activePath === '/admin/account'}>
                    Account
                  </NavLink>
                </NavItem>
              </>
            )}
          </NavList>
          <MenuInquiryButton
            onClick={() => {
              setIsMobileMenuOpen(false);
              toggleCart();
            }}
          >
            License Inquiry{cartCount > 0 ? ` (${cartCount})` : ''}
          </MenuInquiryButton>
        </NavLinks>
        
        <CartButton
          $hasItems={cartCount > 0}
          onClick={toggleCart}
          aria-label="View license inquiry"
        >
          {/* Request-list glyph replaces the shopping cart — supervisors are
              sending an inquiry, not buying products. */}
          <svg viewBox="0 0 24 24">
            <path d="M3 5h14v2H3V5zm0 6h14v2H3v-2zm0 6h9v2H3v-2zm16.5-4.5l1.4 1.4-4.9 4.9-2.9-2.9 1.4-1.4 1.5 1.5 3.5-3.5z" />
          </svg>
          <CartCount $count={cartCount}>{cartCount}</CartCount>
        </CartButton>
      </NavContent>
    </NavbarContainer>
  );
};

export default Navbar;
